import { useNavigate } from "@solidjs/router";
import { type Component, createSignal, onCleanup, onMount } from "solid-js";
import { useCompanyStore } from "@/domains/company/company/store/company-store";
import type { PlanLimitError } from "../api/types";
import { PLAN_SELECTION_PATH } from "../constants";
import {
  isPlanLimitPayload,
  shouldOpenPlanLimitModal,
} from "../utils/plan-limit";
import { PlanLimitModal } from "./PlanLimitModal";

/**
 * L'unica superficie che risponde a una quota di piano esaurita.
 *
 * Ascolta l'evento che l'interceptor emette su ogni `403 plan_limit_exceeded`
 * e apre il modale di upsell. Vale per tutti e tre i limiti del middleware —
 * aziende, membri del team, offerte attive — così ogni flusso che ne incontra
 * uno riceve la stessa risposta senza doversela scrivere da sé.
 *
 * Due scelte di collocazione, entrambe pagate in bug veri:
 *
 * 1. **Dentro il router, non nei provider.** Il modale naviga verso i piani, e
 *    `useNavigate` esiste solo dentro `<Router>`: montato accanto agli altri
 *    provider (fuori) l'unica uscita restava `window.location.href`, cioè un
 *    reload pieno con la bozza da ricaricare da capo.
 * 2. **Non dietro `isBillingEnabled()`.** Quel flag spegne le superfici di
 *    vendita del frontend, non il middleware del backend: il 403 arriva anche
 *    in produzione, dove il flag è spento. Lì il modale spiega il blocco e
 *    basta — la CTA di upgrade sparisce, perché la pagina dei piani in quella
 *    build risponde `/404`.
 */
export const PlanLimitWatcher: Component = () => {
  const navigate = useNavigate();
  const [store] = useCompanyStore();

  const [detail, setDetail] = createSignal<PlanLimitError | null>(null);

  onMount(() => {
    const handler = (event: CustomEvent<unknown>) => {
      const payload = event.detail;
      if (!isPlanLimitPayload(payload)) return;
      if (
        !shouldOpenPlanLimitModal(payload, {
          hasSelectedCompany: Boolean(store.selectedCompany?.id),
        })
      ) {
        return;
      }
      setDetail(payload);
    };

    window.addEventListener("plan-limit-exceeded", handler as EventListener);

    onCleanup(() => {
      window.removeEventListener(
        "plan-limit-exceeded",
        handler as EventListener,
      );
    });
  });

  return (
    <PlanLimitModal
      open={detail() !== null}
      detail={detail()}
      onClose={() => setDetail(null)}
      onUpgrade={() => navigate(PLAN_SELECTION_PATH)}
    />
  );
};
